import { call } from 'redux-saga/effects';
import type { SagaIterator } from 'redux-saga';
import type { UUID } from '@appcues/domain-types';
import { getTypedContext } from 'ext/lib/saga-context';
import type { AddTacticContentResult } from 'ext/lib/api/types';

/** Shared product copy for the non-blocking association-failure toast. */
export const TACTIC_ASSOCIATION_FAILED_MESSAGE =
  "Created, but couldn't add it to the selected tactic — add it later from the tactic.";

interface AssociateTacticArgs {
  tacticId?: UUID | null;
  /** Id of the just-created content (experience id for these surfaces). */
  contentId?: string | null;
}

/**
 * Best-effort association of newly-created content with a tactic.
 *
 * Runs AFTER a successful create. The experience already exists, so a failed
 * link must never block creation or trigger a retry of the create. This helper
 * stays UI-agnostic: it performs the call, logs failures, and returns whether
 * the link succeeded — the caller decides how to surface a non-blocking toast
 * (each app uses its own toast). Returns `true` (nothing to do) when no tactic
 * was selected or no content id resolved. See CampaignAndTacticApis.md §5.
 */
export function* associateTactic({
  tacticId,
  contentId,
}: AssociateTacticArgs): SagaIterator {
  if (!tacticId || !contentId) return true;

  const logger = yield* getTypedContext('logger');
  try {
    const api = yield* getTypedContext('api');
    const result: AddTacticContentResult | undefined = yield call(
      api.addTacticContent,
      tacticId,
      contentId
    );
    // Treat a missing result (e.g. a GraphQL error envelope) as a failure too,
    // not just an explicit `success: false`.
    if (!result?.success) {
      throw new Error(
        result?.message ?? 'addTacticContent did not return success'
      );
    }
    return true;
  } catch (error) {
    logger?.error?.(error);
    return false;
  }
}
